import { parseDate } from './formatters'

/**
 * Utilitários de Validação
 */

/**
 * Valida CPF
 */
export const validateCPF = (cpf) => {
  if (!cpf) return false
  
  const cleaned = cpf.replace(/\D/g, '')
  
  if (cleaned.length !== 11) return false
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cleaned)) return false
  
  // Validação do primeiro dígito verificador
  let sum = 0
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i)
  }
  let remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) remainder = 0
  if (remainder !== parseInt(cleaned.charAt(9))) return false
  
  // Validação do segundo dígito verificador
  sum = 0
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i)
  }
  remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) remainder = 0
  if (remainder !== parseInt(cleaned.charAt(10))) return false
  
  return true
}

/**
 * Valida email
 */
export const validateEmail = (email) => {
  if (!email) return false
  
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(email)
}

/**
 * Valida telefone brasileiro
 */
export const validatePhone = (phone) => {
  if (!phone) return false
  
  const cleaned = phone.replace(/\D/g, '')
  return cleaned.length === 10 || cleaned.length === 11
}

/**
 * Valida senha (mínimo 8 caracteres, 1 maiúscula, 1 minúscula, 1 número)
 */
export const validatePassword = (password) => {
  if (!password || password.length < 8) return false
  
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasNumber = /\d/.test(password)
  
  return hasUpperCase && hasLowerCase && hasNumber
}

/**
 * Valida nome (mínimo 2 palavras)
 */
export const validateFullName = (name) => {
  if (!name) return false
  
  const words = name.trim().split(/\s+/)
  return words.length >= 2 && words.every(word => word.length >= 2)
}

/**
 * Valida data de nascimento (maior de 18 anos)
 */
export const validateBirthDate = (date, minAge = 18) => {
  if (!date) return false
  
  const birthDate = parseDate(date)
  const today = new Date()
  
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  
  return age >= minAge
}

/**
 * Valida cartão de crédito (algoritmo de Luhn)
 */
export const validateCreditCard = (cardNumber) => {
  if (!cardNumber) return false
  
  const cleaned = cardNumber.replace(/\D/g, '')
  
  if (cleaned.length < 13 || cleaned.length > 19) return false
  
  let sum = 0
  let isEven = false
  
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i))
    
    if (isEven) {
      digit *= 2
      if (digit > 9) digit -= 9
    }
    
    sum += digit
    isEven = !isEven
  }
  
  return sum % 10 === 0
}

/**
 * Valida CVV
 */
export const validateCVV = (cvv) => {
  if (!cvv) return false
  
  const cleaned = cvv.replace(/\D/g, '')
  return cleaned.length === 3 || cleaned.length === 4
}

/**
 * Valida data de expiração do cartão
 */
export const validateCardExpiry = (month, year) => {
  // Se receber apenas um argumento, assume que é no formato "MM/YY" ou "MM/YYYY"
  if (year === undefined && typeof month === 'string') {
    const parts = month.split('/')
    if (parts.length !== 2) return false
    month = parts[0]
    year = parts[1]
  }

  if (!month || !year) return false
  
  const cleanedMonth = month.toString().replace(/\D/g, '')
  const cleanedYear = year.toString().replace(/\D/g, '')

  // Obriga ano com 4 dígitos (MM/YYYY) conforme solicitado pelo usuário
  if (cleanedYear.length !== 4) return false
  if (cleanedMonth.length === 0 || cleanedMonth.length > 2) return false

  const currentDate = new Date()
  const currentMonth = currentDate.getMonth() + 1
  const currentYear = currentDate.getFullYear()
  
  const expMonth = parseInt(cleanedMonth)
  const expYear = parseInt(cleanedYear)
  
  if (expMonth < 1 || expMonth > 12) return false
  
  if (expYear < currentYear) return false
  if (expYear === currentYear && expMonth < currentMonth) return false
  
  return true
}

/**
 * Valida CEP
 */
export const validateCEP = (cep) => {
  if (!cep) return false
  
  const cleaned = cep.replace(/\D/g, '')
  return cleaned.length === 8
}

/**
 * Retorna mensagem de erro para validação
 */
export const getValidationError = (field, value) => {
  const validators = {
    cpf: {
      validate: validateCPF,
      message: 'CPF inválido'
    },
    email: {
      validate: validateEmail,
      message: 'E-mail inválido'
    },
    phone: {
      validate: validatePhone,
      message: 'Telefone inválido'
    },
    password: {
      validate: validatePassword,
      message: 'A senha deve ter no mínimo 8 caracteres, incluindo letras maiúsculas, minúsculas e números'
    },
    fullName: {
      validate: validateFullName,
      message: 'Digite seu nome completo'
    },
    birthDate: {
      validate: validateBirthDate,
      message: 'Você deve ter pelo menos 18 anos'
    },
    creditCard: {
      validate: validateCreditCard,
      message: 'Número do cartão inválido'
    },
    cvv: {
      validate: validateCVV,
      message: 'CVV inválido'
    },
    expiry: {
      validate: validateCardExpiry,
      message: 'Data de validade inválida. Use o formato MM/AAAA (ex: 12/2026)'
    },
    cep: {
      validate: validateCEP,
      message: 'CEP inválido'
    }
  }
  
  const validator = validators[field]
  
  if (!validator) return null
  if (validator.validate(value)) return null
  
  return validator.message
}

export default {
  validateCPF,
  validateEmail,
  validatePhone,
  validatePassword,
  validateFullName,
  validateBirthDate,
  validateCreditCard,
  validateCVV,
  validateCardExpiry,
  validateCEP,
  getValidationError
}
